import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  serverTimestamp,
  DocumentReference 
} from 'firebase/firestore';
import { getEmailTemplate } from './templateService';
import { EmailType, getStatusDisplayName } from './emailService';

/**
 * Interface for template validation result
 */
export interface TemplateValidationResult {
  isValid: boolean;
  missing: string[];
  templateType: string;
  locale: string;
  usedLocale: string;
}

/**
 * Extracts all Handlebars variables from a template string
 * @param template Template string (subject or body)
 * @returns Array of variable names found in template
 */
function extractTemplateVariables(template: string): string[] {
  if (!template) return [];
  
  // Regular expression to find {{variable}} patterns
  const regex = /\{\{([^}]+)\}\}/g;
  const variables: string[] = [];
  let match;
  
  while ((match = regex.exec(template)) !== null) {
    let variable = match[1].trim();

    // Ignore Handlebars partial includes like {{>header}} and comments like {{! note }}
    if (variable.startsWith('>') || variable.startsWith('!')) {
      continue;
    }
    
    // Handle Handlebars helpers and expressions
    // Remove #if, #unless, #each, /if, /unless, /each helpers
    if (variable.startsWith('#') || variable.startsWith('/')) {
      continue;
    }
    
    // Handle expressions like {{#if condition}} - extract just the variable part
    if (variable.includes(' ')) {
      // For complex expressions, try to extract the first variable
      const parts = variable.split(' ');
      for (const part of parts) {
        if (!part.startsWith('#') && !part.startsWith('/') && !part.includes('&&') && !part.includes('||')) {
          variable = part;
          break;
        }
      }
    }
    
    // Clean up the variable name
    variable = variable.replace(/[{}]/g, '').trim();
    
    if (variable && !variables.includes(variable)) {
      variables.push(variable);
    }
  }
  
  return variables;
}

/**
 * Validates if the provided context contains all variables used in the email template
 * @param type Email type
 * @param locale Preferred locale
 * @param context Data context for the template
 * @returns Validation result with missing variables
 */
export async function validateTemplateVariables(
  type: EmailType, 
  locale: string, 
  context: any
): Promise<TemplateValidationResult> {
  try {
    // Get the template that would be used for this email
    const template = await getEmailTemplate(type, locale);
    
    // Extract variables from both subject and body templates
    const subjectVariables = extractTemplateVariables(template.subjectTemplate);
    const bodyVariables = extractTemplateVariables(template.bodyTemplate);
    
    // Combine all variables, removing duplicates
    const allVariables = Array.from(new Set([...subjectVariables, ...bodyVariables]));
    
    // Check if all variables are present in context
    const missing: string[] = [];
    
    for (const variable of allVariables) {
      if (!(variable in context) || context[variable] === undefined || context[variable] === null) {
        missing.push(variable);
      }
    }
    
    // Special check: if template uses {{status}} and context has status, verify it's localized
    if (allVariables.includes('status') && context.status && typeof context.status === 'string') {
      const expectedLocalized = getStatusDisplayName(context.status, locale);
      if (context.status !== expectedLocalized) {
        console.warn(`[validateTemplateVariables] ⚠️ Status may not be localized: "${context.status}" (expected: "${expectedLocalized}") for locale ${locale}`);
        // Don't add to missing since it's present, just log warning
      }
    }
    
    return {
      isValid: missing.length === 0,
      missing,
      templateType: type,
      locale,
      usedLocale: template.locale
    };
  } catch (error) {
    console.error('[validateTemplateVariables] Error validating template:', error);
    // If template doesn't exist or validation fails, consider it invalid
    return {
      isValid: false,
      missing: ['TEMPLATE_NOT_FOUND'],
      templateType: type,
      locale,
      usedLocale: locale
    };
  }
}

/**
 * Creates an admin task for missing template variables if one doesn't already exist
 * @param validation Validation result
 * @param emailType Email type
 * @param recipientEmail Recipient email address
 * @param context Data context that was provided
 * @returns Reference to created or existing admin task
 */
export async function createMissingVariablesAdminTask(
  validation: TemplateValidationResult,
  emailType: EmailType,
  recipientEmail: string,
  context: any
): Promise<DocumentReference | null> {
  const db = getFirestore();
  
  // Create a unique task identifier based on template type and missing variables
  // const missingVariablesKey = validation.missing.sort().join(','); // Available if needed for future use
  // const taskIdentifier = `email-template-variables-${emailType}-${missingVariablesKey}`; // Available if needed for future use
  
  try {
    // Check if an open admin task already exists for this issue
    const existingTasksQuery = query(
      collection(db, 'adminTasks'),
      where('type', '==', 'email-template-variables'),
      where('status', '==', 'open'),
      where('emailType', '==', emailType),
      where('missingVariables', 'array-contains-any', validation.missing)
    );
    
    const existingTasks = await getDocs(existingTasksQuery);
    
    // Check if any existing task has the same missing variables
    for (const taskDoc of existingTasks.docs) {
      const taskData = taskDoc.data();
      const existingMissing = taskData.missingVariables || [];
      
      // Check if the missing variables match (order doesn't matter)
      const matches = validation.missing.every((variable: string) => existingMissing.includes(variable)) &&
                     existingMissing.every((variable: string) => validation.missing.includes(variable));
      
      if (matches) {
        return taskDoc.ref; // Return existing task reference
      }
    }
    
    // Create new admin task
    const taskData = {
      type: 'email-template-variables',
      status: 'open',
      emailType: emailType,
      templateType: validation.templateType,
      locale: validation.usedLocale,
      recipientEmail: recipientEmail,
      missingVariables: validation.missing,
      contextKeys: Object.keys(context),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      description: {
        title: `Missing Template Variables`,
        summary: `Email template "${emailType}" (${validation.usedLocale}) is missing required variables`,
        details: {
          emailType,
          templateType: validation.templateType,
          locale: validation.locale,
          usedLocale: validation.usedLocale,
          recipientEmail,
          missingVariables: validation.missing,
          providedVariables: Object.keys(context),
          severity: validation.missing.includes('email') ? 'critical' : 'warning'
        }
      },
      link: `/admin/templates`,
      priority: validation.missing.includes('email') ? 'high' : 'medium'
    };
    
    const taskRef = await addDoc(collection(db, 'adminTasks'), taskData);
    
    return taskRef;
  } catch (error) {
    console.error('[createMissingVariablesAdminTask] Error creating admin task:', error);
    return null;
  }
}

/**
 * Validates template variables and creates admin task if needed
 * @param type Email type
 * @param locale Preferred locale
 * @param context Data context for the template
 * @param recipientEmail Recipient email address
 * @returns Validation result
 */
export async function validateAndReportMissingVariables(
  type: EmailType,
  locale: string,
  context: any,
  recipientEmail: string
): Promise<TemplateValidationResult> {
  // Validate the template variables
  const validation = await validateTemplateVariables(type, locale, context);
  
  // If validation fails and there are missing variables, create admin task
  if (!validation.isValid && validation.missing.length > 0) {
    console.warn(`[validateAndReportMissingVariables] Missing variables for ${type}:`, validation.missing);
    
    await createMissingVariablesAdminTask(validation, type, recipientEmail, context);
  }
  
  return validation;
}
